import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, User, UserPlus, CheckCircle2, XCircle, ArrowLeft, Briefcase } from 'lucide-react';
import { useAuth } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';
import api from '@/services/api';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import { useTranslation } from 'react-i18next';
import { isAllowedEmailProvider, normalizeEmail } from '@/utils/emailValidation';

const PASSWORD_REQUIREMENT_TESTS = [
  (pwd: string) => pwd.length >= 12,
  (pwd: string) => /[A-Z]/.test(pwd),
  (pwd: string) => /[a-z]/.test(pwd),
  (pwd: string) => /[0-9]/.test(pwd),
  (pwd: string) => /[!@#$%^&*()\-_=+[\]{};:'",.<>/?\\|`~]/.test(pwd),
];

const registerSchema = z
  .object({
    email: z
      .string()
      .email('Invalid email address')
      .refine(isAllowedEmailProvider, 'Please use Gmail, Outlook, Hotmail, Yahoo, or iCloud'),
    password: z
      .string()
      .min(12, 'Password must be at least 12 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(/[!@#$%^&*()\-_=+[\]{};:'",.<>/?\\|`~]/, 'Password must contain at least one special character'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { setAuth, user, _hasHydrated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [apiPasswordErrors, setApiPasswordErrors] = useState<string[]>([]);
  const { t } = useTranslation(['auth', 'common']);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  const passwordRequirements = [
    { label: t('auth:register.reqMinChars'), test: PASSWORD_REQUIREMENT_TESTS[0] },
    { label: t('auth:register.reqUppercase'), test: PASSWORD_REQUIREMENT_TESTS[1] },
    { label: t('auth:register.reqLowercase'), test: PASSWORD_REQUIREMENT_TESTS[2] },
    { label: t('auth:register.reqNumber'), test: PASSWORD_REQUIREMENT_TESTS[3] },
    { label: t('auth:register.reqSpecialChar'), test: PASSWORD_REQUIREMENT_TESTS[4] },
  ];

  // Show loading while hydrating
  if (!_hasHydrated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  // Redirect if already logged in
  if (user) {
    if (user.role === 'CANDIDATE') {
      navigate('/candidate/dashboard');
    } else if (user.role === 'RECRUITER') {
      navigate('/recruiter/dashboard');
    }
    return null;
  }

  // Clear API password errors when user starts retyping
  const handlePasswordChange = () => {
    if (apiPasswordErrors.length > 0) setApiPasswordErrors([]);
  };

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);

    try {
      const response = await api.post('/auth/register', {
        email: normalizeEmail(data.email),
        password: data.password,
        role: 'CANDIDATE',
        firstName: data.firstName,
        lastName: data.lastName,
      });

      const { user, accessToken, refreshToken } = response.data;

      setAuth(user, accessToken, refreshToken);

      toast.success('Welcome to the platform! 🎉 Please check your email to verify your account.');

      // Use setTimeout to ensure state is persisted before navigation
      setTimeout(() => {
        navigate('/candidate/dashboard');
      }, 100);
    } catch (error: any) {
      const apiError = error.response?.data?.error;
      if (apiError?.code === 'PASSWORD_WEAK' && apiError?.details?.length) {
        setApiPasswordErrors(apiError.details);
      } else {
        toast.error(apiError?.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-purple-50/60 fixed inset-0 overflow-auto">
      {/* Glow blobs */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-200 rounded-full blur-3xl opacity-50" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-purple-200 rounded-full blur-3xl opacity-40" />
      </div>
      <div className="relative w-full max-w-lg my-auto">
        {/* Back to Choice */}
        <Link
          to="/register"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors mb-6 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          <span>{t('auth:register.backToChoice')}</span>
        </Link>

        {/* Register Form */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl animate-slide-up border border-gray-200 shadow-lg">

          <div className="p-8">
            {/* Role header */}
            <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-100">
              <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider">{t('auth:register.candidateTitle')}</p>
                <p className="text-sm text-gray-500">{t('auth:register.subtitle')}</p>
              </div>
            </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                {...register('firstName')}
                type="text"
                label={t('auth:register.firstNameLabel')}
                placeholder={t('auth:register.firstNamePlaceholder')}
                error={errors.firstName?.message}
                icon={<User className="w-5 h-5" />}
                autoComplete="given-name"
                disabled={isLoading}
              />
              <Input
                {...register('lastName')}
                type="text"
                label={t('auth:register.lastNameLabel')}
                placeholder={t('auth:register.lastNamePlaceholder')}
                error={errors.lastName?.message}
                icon={<User className="w-5 h-5" />}
                autoComplete="family-name"
                disabled={isLoading}
              />
            </div>

            {/* Email */}
            <Input
              {...register('email')}
              type="email"
              label={t('auth:register.emailLabel')}
              placeholder={t('auth:register.emailPlaceholder')}
              error={errors.email?.message}
              icon={<Mail className="w-5 h-5" />}
              autoComplete="email"
              disabled={isLoading}
            />

            {/* Password */}
            <div>
              <Input
                {...register('password', {
                  onChange: handlePasswordChange,
                })}
                type="password"
                label={t('auth:register.passwordLabel')}
                placeholder={t('auth:register.passwordPlaceholder')}
                error={errors.password?.message}
                icon={<Lock className="w-5 h-5" />}
                autoComplete="new-password"
                disabled={isLoading}
              />
              <div className="mt-3 space-y-2">
                {/* API errors returned by backend */}
                {apiPasswordErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                    <p className="font-medium text-red-700 mb-2">{t('auth:register.passwordRejectedTitle')}</p>
                    <ul className="space-y-1">
                      {apiPasswordErrors.map((err, i) => (
                        <li key={i} className="flex items-start gap-2 text-red-600">
                          <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>{err}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Client-side requirements checklist */}
                <div className="text-sm">
                  <p className="font-medium text-gray-700 mb-2">{t('auth:register.passwordRequirementsTitle')}</p>
                  <ul className="space-y-1">
                    {passwordRequirements.map((req) => {
                      const met = password && req.test(password);
                      return (
                        <li key={req.label} className={`flex items-center gap-2 ${met ? 'text-green-600' : 'text-gray-500'}`}>
                          {met
                            ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                            : <XCircle className="w-4 h-4 flex-shrink-0 text-gray-400" />}
                          <span>{req.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <Input
                {...register('confirmPassword')}
                type="password"
                label={t('auth:register.confirmPasswordLabel')}
                placeholder={t('auth:register.confirmPasswordPlaceholder')}
                error={errors.confirmPassword?.message}
                icon={<Lock className="w-5 h-5" />}
                autoComplete="new-password"
                disabled={isLoading}
              />
              {confirmPassword && password && (
                <div className={`mt-2 flex items-center gap-2 text-sm ${confirmPassword === password ? 'text-green-600' : 'text-red-500'}`}>
                  {confirmPassword === password
                    ? <><CheckCircle2 className="w-4 h-4" /><span>{t('auth:register.passwordsMatch')}</span></>
                    : <><XCircle className="w-4 h-4" /><span>{t('auth:register.passwordsDoNotMatch')}</span></>}
                </div>
              )}
            </div>

            {/* Terms */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                required
                className="mt-1 w-4 h-4 rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500"
              />
              <label className="text-sm text-gray-700">
                {t('auth:register.agreeToTermsPrefix')}{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 font-semibold transition-colors underline"
                >
                  {t('auth:register.termsOfService')}
                </Link>{' '}
                {t('auth:register.and')}{' '}
                <Link
                  to="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 font-semibold transition-colors underline"
                >
                  {t('auth:register.privacyPolicy')}
                </Link>
              </label>
            </div>

            <Button
              type="submit"
              variant="primary"
              fullWidth
              isLoading={isLoading}
              className="group"
            >
              {!isLoading && <UserPlus className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />}
              {t('auth:register.submitButton')}
            </Button>
          </form>
          </div>

          <div className="px-8 pb-6 pt-2 border-t border-gray-100 bg-white/40">
            <p className="text-center text-sm text-gray-600">
              {t('auth:register.haveAccount')}{' '}
              <Link
                to="/login"
                className="text-blue-600 hover:text-blue-700 font-semibold transition-colors"
              >
                {t('auth:register.signInLink')}
              </Link>
            </p>
          </div>
        </div>

        {/* What happens next */}
        <div className="mt-5 px-1 animate-slide-up delay-100">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('auth:register.whatHappensNext')}</p>
          <div className="grid grid-cols-3 gap-3 text-center">
            {[
              t('auth:register.verifyEmailStep'),
              t('auth:register.completeProfileStep'),
              t('auth:register.getDiscoveredStep'),
            ].map((step, idx) => (
              <div key={idx} className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-xl p-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/15 border border-blue-200 text-blue-700 text-xs font-bold flex items-center justify-center mx-auto mb-2">
                  {idx + 1}
                </div>
                <p className="text-xs text-gray-600 leading-snug">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
